import { useCallback, useEffect } from 'react';

import {
  alpha,
  Box,
  Divider,
  IconButton,
  makeStyles,
  Slide,
  Typography,
  useTheme,
} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import { MediaTypeConfig } from 'constants/mediaTypes';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  contatosSelector,
  fetchMidiasContato,
  MidiasContatoEnum,
  resetMidiasContato,
} from 'store/reducers/contatos';
import {
  atualizarDocumentosAtendimento,
  AtualizarMidiaMensagemInterface,
  baixandoMensagemDocumentosAtendimento,
  BaixandoMidiaMensagemInterface,
} from 'store/reducers/mensagens';

import ListaDocumentosContato from './ListaDocumentosContato';
import ListaMidiasContato from './ListaMidiasContato';

const useStyles = makeStyles(({ palette, shape, spacing }) => ({
  /**
   * Wrapper absolutamente posicionado que cobre toda a coluna esquerda,
   * exatamente como o searchDrawerWrapper do ModalSearchDrawer.
   */
  wrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 10,
    pointerEvents: 'none',

    '& > *': {
      pointerEvents: 'auto',
    },
  },

  /** Painel deslizante que imita a aparência de um Drawer lateral. */
  panel: {
    height: '100%',
    width: '100%',
    backgroundColor: palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopRightRadius: shape.borderRadius,
    borderBottomRightRadius: shape.borderRadius,
    borderRight: `1px solid ${alpha(palette.divider, 0.07)}`,
  },

  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    padding: spacing(2),
    flexShrink: 0,
  },

  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

type InlineDrawerMidiasContatoProps = {
  open: boolean;
  onClose: () => void;
  contatoId: string;
  canalId: string;
  mediaType: MediaTypeConfig;
};

export default function InlineDrawerMidiasContato({
  open,
  onClose,
  canalId,
  contatoId,
  mediaType,
}: InlineDrawerMidiasContatoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const IconComponent = mediaType.icon;
  const color = theme.palette[mediaType.colorKey].main;

  const { midiasContato, loadingMidiasContato, midiasContatoCursor } =
    useAppSelector(contatosSelector);

  const getMidiasContato = useCallback(
    (params?: { cursor?: CursorInterface }) => {
      if (!contatoId || !canalId) return;

      dispatch(
        fetchMidiasContato({
          contatoId,
          channelCode: canalId,
          type: mediaType.key,
          cursor: params?.cursor,
        }),
      );
    },
    [canalId, contatoId, dispatch, mediaType.key],
  );

  const fetchNextMidiasContato = useCallback(() => {
    const { afterCursor } = midiasContatoCursor;
    if (afterCursor) {
      getMidiasContato({ cursor: { afterCursor } });
    }
  }, [getMidiasContato, midiasContatoCursor]);

  useEffect(() => {
    if (open && contatoId && canalId) {
      getMidiasContato();
    }

    return () => {
      dispatch(resetMidiasContato());
    };
  }, [open, canalId, contatoId, getMidiasContato, dispatch]);

  useEffect(() => {
    const onBaixandoMidiaMensagem = (data: BaixandoMidiaMensagemInterface) => {
      dispatch(baixandoMensagemDocumentosAtendimento(data));
    };
    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      dispatch(atualizarDocumentosAtendimento(data));
    };

    socket.on(socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM, onBaixandoMidiaMensagem);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [dispatch]);

  const fileCount = midiasContato.length;

  return (
    <Box className={classes.wrapper}>
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
        <Box className={classes.panel}>
          <Box className={classes.headerContent}>
            <IconButton onClick={onClose}>
              <ArrowBackIosIcon style={{ width: 16, height: 16 }} />
            </IconButton>

            <Box
              className={classes.iconContainer}
              style={{
                backgroundColor: alpha(color, 0.1),
                color,
              }}
            >
              <IconComponent />
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="textPrimary"
                style={{ fontWeight: 500 }}
              >
                {mediaType.title}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {fileCount} {fileCount === 1 ? 'arquivo' : 'arquivos'}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box flex={1} minHeight={0} height="100%" overflow="hidden">
            {mediaType.key === MidiasContatoEnum.DOCUMENTO ? (
              <ListaDocumentosContato
                documentos={midiasContato}
                loading={loadingMidiasContato}
                hasMore={Boolean(midiasContatoCursor?.afterCursor)}
                loadMore={fetchNextMidiasContato}
              />
            ) : (
              <ListaMidiasContato
                midias={midiasContato}
                loading={loadingMidiasContato}
                hasMore={Boolean(midiasContatoCursor?.afterCursor)}
                loadMore={fetchNextMidiasContato}
                columns={4}
              />
            )}
          </Box>
        </Box>
      </Slide>
    </Box>
  );
}
